import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  getUserData,
  updateUserData,
  getUserRolesData,
} from '../services/userService.js'
import type { User, UserAddress } from '@/types/user'
import type { PersonRole } from '@/types/role'
import { formatDate } from '@/utils/dateUtils.js'

const ARREARS_MEMBERSHIP_STATUSES = ['ARREARS', 'PART-ARREARS']
const LAPSED_MEMBERSHIP_STATUSES = ['LAPSED', 'POST-LAPSE']

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const roles = ref<PersonRole[]>([])

  const hasError = computed(() => !!error.value)
  const isAuthenticated = computed(() => !!user.value)
  const userName = computed(() => user.value?.name ?? '')
  const userGuid = computed(() => user.value?.guid ?? '')
  const userMobileNumber = computed(() => user.value?.mobile_number ?? '')
  const userEmail = computed(() => user.value?.email ?? '')
  const userPermissions = computed(() => user.value?.permissions ?? {})
  const userPhoneNumber = computed(() => user.value?.phone_number ?? '')
  const userFirstName = computed(() => user.value?.name.split(' ')[0] ?? '')
  const userPostcode = computed(() => user.value?.address.postcode ?? '')
  const userDateOfBirth = computed(() => user.value?.date_of_birth ?? '')
  const userEncryptedEventsToken = computed(
    () => user.value?.encrypted_events_token ?? ''
  )
  const userIsAboutToLapse = computed(() =>
    ARREARS_MEMBERSHIP_STATUSES.includes(
      user.value?.membership_status.toUpperCase() ?? ''
    )
  )
  const userIsLapsed = computed(() =>
    LAPSED_MEMBERSHIP_STATUSES.includes(
      user.value?.membership_status.toUpperCase() ?? ''
    )
  )
  const userIsOrIsAboutToLapse = computed(
    () => userIsAboutToLapse.value || userIsLapsed.value
  )
  const userAddress = computed(() => user.value?.address ?? {})
  const userJoinDate = computed(() =>
    formatDate(user.value?.join_date?.split(' ')[0], {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  )
  const userRoles = computed(() => roles.value)
  const userActiveRoles = computed(() => {
    const now = new Date()
    return roles.value.filter((role) => {
      if (!role.expiry_date) return true
      return new Date(role.expiry_date) > now
    })
  })

  function setUserAddress(address: UserAddress) {
    if (user.value) {
      user.value.address = address
    }
  }

  function setUserRoles(newRoles: PersonRole[]) {
    roles.value = newRoles
  }

  function userHasOneOrMoreOfPermissions(permissions: string[]): boolean {
    const perms = userPermissions.value
    if (!perms) return false
    return permissions.some((permission) => Boolean(perms[permission]))
  }

  function setUserPhone(phone: string) {
    if (user.value) {
      user.value.phone_number = phone
    }
  }

  function setUserMobile(mobile: string) {
    if (user.value) {
      user.value.mobile_number = mobile
    }
  }

  function setUser(newUser: User) {
    user.value = newUser
    error.value = null
  }

  function clearUser() {
    user.value = null
    error.value = null
    roles.value = []
  }

  function setError(err: Error) {
    error.value = err
    user.value = null
  }

  async function checkAuthenticationStatus() {
    if (isLoading.value) {
      await waitForLoadingToFinish()
    } else if (!user.value) {
      await fetchUser()
    }
  }

  async function fetchUserRoles() {
    try {
      const fetchedRoles = await getUserRolesData()
      setUserRoles(fetchedRoles)
    } catch (err) {
      // Deliberately not setError: that would null the user and
      // de-authenticate the client because a roles request failed
      console.error('Error fetching user roles:', err)
    }
  }

  async function fetchUser() {
    if (isLoading.value) return

    isLoading.value = true
    try {
      const fetchedUser = await getUserData()
      setUser(fetchedUser)
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      console.error('Error fetching user:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function updateUser() {
    isLoading.value = true
    try {
      const result = await updateUserData(user.value)
      return result
    } catch (err) {
      console.error('Error updating user:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  function waitForLoadingToFinish(): Promise<void> {
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (!isLoading.value) {
          clearInterval(interval)
          resolve()
        }
      }, 50)
    })
  }

  return {
    user,
    isLoading,
    error,
    roles,
    hasError,
    isAuthenticated,
    userName,
    userGuid,
    userMobileNumber,
    userEmail,
    userPermissions,
    userPhoneNumber,
    userFirstName,
    userPostcode,
    userDateOfBirth,
    userEncryptedEventsToken,
    userIsAboutToLapse,
    userIsLapsed,
    userIsOrIsAboutToLapse,
    userAddress,
    userJoinDate,
    userRoles,
    userActiveRoles,
    setUserAddress,
    setUserRoles,
    userHasOneOrMoreOfPermissions,
    setUserPhone,
    setUserMobile,
    setUser,
    clearUser,
    setError,
    checkAuthenticationStatus,
    fetchUserRoles,
    fetchUser,
    updateUser,
    waitForLoadingToFinish,
  }
})
